import {
  MailDelivery,
  MailedNoticeDocumentType,
  ShippingTier
} from '../../types/mail';
import { ColumnService } from '../../services/directory';
import { getErrorReporter } from '../../utils/errors';
import { ResponseOrError, wrapError, wrapSuccess } from '../../types/responses';
import { InternalServerError } from '../../errors/ColumnErrors';
import { LineItem } from '../../types/invoices';
import { InvoiceItemTypeData } from '../../enums/InvoiceItemType';
import {
  computeStandardAffidavitShipping,
  computeRonAffidavitPricing,
  computeWetSigAffidavitPricing
} from './affidavit';
import {
  computeInvoiceShipping,
  MAILED_INVOICE_LINE_ITEM_DESCRIPTION
} from './invoice';
import { getBillableMail } from '../../mail/noticeTypeDefaultMail';
import {
  AddOnAmount,
  NoticeAddOnPricing,
  NoticeAddOnPricingConfig,
  NoticeAddOnPricingContext
} from './types';
import { AffidavitReconciliationSettings } from '../../types/organization';
import { requiresWetSignature } from '../affidavitSettings';

/**
 * Derives the AA pricing context (RON-fee waiver + first-wet-sig-free rules)
 * from a publisher's affidavit reconciliation settings. Used both server-side
 * (invoice pricing) and client-side (placement sidebar) so the two stay in sync.
 */
export function deriveNoticeAddOnPricingContext(
  settings:
    | Partial<
        Pick<
          AffidavitReconciliationSettings,
          'affidavitsManagedByColumn' | 'notarizationVendor'
        >
      >
    | null
    | undefined
): NoticeAddOnPricingContext {
  const affidavitsManagedByColumn =
    settings?.affidavitsManagedByColumn ?? false;
  return {
    affidavitsManagedByColumn,
    firstWetSigAffidavitFree:
      affidavitsManagedByColumn && requiresWetSignature(settings)
  };
}

/**
 * Whether this notice carries mail that computeNoticeAddOnPricing can price at
 * all. Untyped legacy mail and publisher-funded copies never produce an add-on,
 * so a notice holding only those needs no quote.
 */
export function hasBillableAddOnMail(mail: MailDelivery[]): boolean {
  return getBillableMail(mail).some(m => !!m.documentType);
}

export function computeNoticeAddOnPricing(
  mail: MailDelivery[],
  config: NoticeAddOnPricingConfig,
  context: NoticeAddOnPricingContext
): ResponseOrError<NoticeAddOnPricing> {
  const billableMail = getBillableMail(mail);

  // Records that carry an add-on *fee* here. Standard affidavits are excluded
  // because they have no add-on fee — their Column fee remains the legacy
  // automatedAffidavitFeeInCents (see getAffidavitFeeInCentsAndFeeSplitFromSettingsAndMail).
  // Standard affidavit *shipping* is handled separately via standardAffidavitShipping.
  const addOnRecords = billableMail.filter(
    (m): m is MailDelivery & { documentType: MailedNoticeDocumentType } =>
      !!m.documentType &&
      m.documentType !== MailedNoticeDocumentType.STANDARD_AFFIDAVIT
  );

  // Only explicitly typed records: untyped legacy mail predates the add-ons
  // flow and is billed solely through automatedAffidavitFeeInCents, so pricing
  // shipping for it here would newly charge existing notices.
  const standardAffidavitRecords = billableMail.filter(
    m => m.documentType === MailedNoticeDocumentType.STANDARD_AFFIDAVIT
  );

  const result: NoticeAddOnPricing = {};

  // Computed before the no-add-on-records bail-out: a mailed standard affidavit
  // copy is itself a paid add-on ("Mail a copy — $X per copy"), and it is
  // commonly the only add-on on an order.
  const standardAffidavitShipping = computeStandardAffidavitShipping(
    standardAffidavitRecords,
    config
  );
  if (standardAffidavitShipping) {
    result.standardAffidavitShipping = standardAffidavitShipping;
  }

  if (addOnRecords.length === 0) {
    return wrapSuccess(result);
  }

  const ronRecords = addOnRecords.filter(
    m => m.documentType === MailedNoticeDocumentType.RON_AFFIDAVIT
  );
  const wetSigRecords = addOnRecords.filter(
    m => m.documentType === MailedNoticeDocumentType.WET_SIG_AFFIDAVIT
  );
  const invoiceRecords = addOnRecords.filter(
    m => m.documentType === MailedNoticeDocumentType.INVOICE
  );

  if (ronRecords.length > 0 && wetSigRecords.length > 0) {
    const err = new InternalServerError(
      'Cannot have both RON_AFFIDAVIT and WET_SIG_AFFIDAVIT records on the same order'
    );
    getErrorReporter().logAndCaptureCriticalError(
      ColumnService.PAYMENTS,
      err,
      'Mixed affidavit types detected in computeNoticeAddOnPricing',
      {}
    );
    return wrapError(err);
  }

  if (ronRecords.length > 0) {
    const ronPricing = computeRonAffidavitPricing(ronRecords, config, context);
    Object.assign(result, ronPricing);
  }

  if (wetSigRecords.length > 0) {
    const [wetSigErr, wetSigPricing] = computeWetSigAffidavitPricing(
      wetSigRecords,
      config,
      context
    );
    if (wetSigErr) {
      return wrapError(wetSigErr);
    }
    Object.assign(result, wetSigPricing);
  }

  const invoiceShipping = computeInvoiceShipping(invoiceRecords, config);
  if (invoiceShipping) {
    result.invoiceShipping = invoiceShipping;
  }

  return wrapSuccess(result);
}

/**
 * Sums every component of a NoticeAddOnPricing (affidavit fees + shipping) into a
 * single cents total. Canonical source of truth for the add-on amount; use this
 * (rather than inspecting line-item types) wherever the add-on total must be
 * excluded from a base such as the percentage-based convenience fee.
 *
 * Every component extends AddOnAmount (totalInCents), so this sum auto-includes any
 * new add-on field; the typed accessor below makes a non-conforming field a compile
 * error here.
 */
export function sumNoticeAddOnPricing(
  addOns: NoticeAddOnPricing | null | undefined
): number {
  if (!addOns) {
    return 0;
  }
  const components: Array<AddOnAmount | undefined> = Object.values(addOns);
  return components.reduce(
    (total, component) => total + (component?.totalInCents ?? 0),
    0
  );
}

/**
 * Which subscription line each add-on component is collected on. Exhaustive over
 * NoticeAddOnPricing so a new component is a compile error rather than uncollected revenue.
 */
const NOTICE_ADD_ON_BUCKETS: Record<
  keyof Required<NoticeAddOnPricing>,
  'affidavit' | 'shipping'
> = {
  wetSigAffidavit: 'affidavit',
  ronAffidavit: 'affidavit',
  wetSigAffidavitShipping: 'shipping',
  ronAffidavitShipping: 'shipping',
  standardAffidavitShipping: 'shipping',
  invoiceShipping: 'shipping'
};

function sumNoticeAddOnBucket(
  addOns: NoticeAddOnPricing | null | undefined,
  bucket: 'affidavit' | 'shipping'
): number {
  if (!addOns) {
    return 0;
  }
  return Object.entries(addOns).reduce((total, [key, component]) => {
    const componentBucket =
      NOTICE_ADD_ON_BUCKETS[key as keyof NoticeAddOnPricing];
    if (!componentBucket) {
      getErrorReporter().logAndCaptureCriticalError(
        ColumnService.PAYMENTS,
        new InternalServerError(
          `Notice add-on component "${key}" is not assigned to a billing bucket`
        ),
        'Unbucketed notice add-on component will not be collected',
        {}
      );
      return total;
    }
    if (componentBucket !== bucket) {
      return total;
    }
    return total + ((component as AddOnAmount | undefined)?.totalInCents ?? 0);
  }, 0);
}

// Billed on InvoiceItemType.affidavit_fee_oop
export function sumNoticeAddOnAffidavitFees(
  addOns: NoticeAddOnPricing | null | undefined
): number {
  return sumNoticeAddOnBucket(addOns, 'affidavit');
}

// Billed on InvoiceItemType.shipping_fee_oop
export function sumNoticeAddOnShippingFees(
  addOns: NoticeAddOnPricing | null | undefined
): number {
  return sumNoticeAddOnBucket(addOns, 'shipping');
}

/**
 * Expands a NoticeAddOnPricing into one itemized LineItem per non-zero component,
 * with unitPricing where applicable.
 */
export function expandNoticeAddOnsToLineItems(
  addOns: NoticeAddOnPricing,
  lineItemDate: Date
): LineItem[] {
  const items: LineItem[] = [];

  const affidavitType = InvoiceItemTypeData.affidavit_fee_oop.value;
  const shippingType = InvoiceItemTypeData.shipping_fee_oop.value;

  if (addOns.wetSigAffidavit && addOns.wetSigAffidavit.totalInCents > 0) {
    items.push({
      amount: addOns.wetSigAffidavit.totalInCents,
      date: lineItemDate,
      description: 'Additional wet signature affidavit fee',
      unitPricing: {
        quantity: addOns.wetSigAffidavit.qty,
        price: addOns.wetSigAffidavit.unitPriceInCents
      },
      type: affidavitType
    });
  }

  if (addOns.ronAffidavit && addOns.ronAffidavit.totalInCents > 0) {
    items.push({
      amount: addOns.ronAffidavit.totalInCents,
      date: lineItemDate,
      description: 'Remote online notarization affidavit',
      type: affidavitType
    });
  }

  if (
    addOns.wetSigAffidavitShipping &&
    addOns.wetSigAffidavitShipping.totalInCents > 0
  ) {
    const shippingSpeedNames: Record<ShippingTier, string> = {
      [ShippingTier.GROUND]: 'Ground',
      [ShippingTier.EXPRESS]: 'Express',
      [ShippingTier.RUSH]: 'Rush'
    };
    // Fall back to Ground if the tier is ever missing/unexpected so the label
    // never reads "Additional affidavit undefined shipping".
    const shippingSpeedName =
      shippingSpeedNames[addOns.wetSigAffidavitShipping.shippingTier] ??
      'Ground';
    items.push({
      amount: addOns.wetSigAffidavitShipping.totalInCents,
      date: lineItemDate,
      description: `Additional affidavit ${shippingSpeedName} shipping`,
      unitPricing: {
        quantity: addOns.wetSigAffidavitShipping.qty,
        price: addOns.wetSigAffidavitShipping.unitPriceInCents
      },
      type: shippingType
    });
  }

  if (
    addOns.ronAffidavitShipping &&
    addOns.ronAffidavitShipping.totalInCents > 0
  ) {
    items.push({
      amount: addOns.ronAffidavitShipping.totalInCents,
      date: lineItemDate,
      description: 'Mailed RON affidavit',
      unitPricing: {
        quantity: addOns.ronAffidavitShipping.qty,
        price: addOns.ronAffidavitShipping.unitPriceInCents
      },
      type: shippingType
    });
  }

  if (
    addOns.standardAffidavitShipping &&
    addOns.standardAffidavitShipping.totalInCents > 0
  ) {
    items.push({
      amount: addOns.standardAffidavitShipping.totalInCents,
      date: lineItemDate,
      description: 'Mailed affidavit copy',
      unitPricing: {
        quantity: addOns.standardAffidavitShipping.qty,
        price: addOns.standardAffidavitShipping.unitPriceInCents
      },
      type: shippingType
    });
  }

  if (addOns.invoiceShipping && addOns.invoiceShipping.totalInCents > 0) {
    items.push({
      amount: addOns.invoiceShipping.totalInCents,
      date: lineItemDate,
      description: MAILED_INVOICE_LINE_ITEM_DESCRIPTION,
      unitPricing: {
        quantity: addOns.invoiceShipping.qty,
        price: addOns.invoiceShipping.unitPriceInCents
      },
      type: shippingType
    });
  }

  return items;
}
